/**
 * BOM detail — lines, expansion, budget bar, and conversion to a PO.
 *
 * Functional spec: `qt_poc/bom_qt.py`.
 *
 * The one thing this screen must not get wrong: **there are two totals and
 * they are different numbers.**
 *
 *   - `total_with_extra_usd` is NET of stock — what still has to be bought.
 *     It is what the budget bar measures, because that is the money that will
 *     actually leave.
 *   - `order_total_usd` is GROSS — what converting to a PO will cost, because
 *     the PO orders the full required quantity. Stock netting is a planning
 *     figure; the order is not netted (the Option A decision).
 *
 * They diverge exactly when something is in stock. Showing only the first is
 * how someone approves 1,755 and receives an order for 2,340, so the convert
 * button states the order total on its face.
 */
import { useMemo, useState } from 'react'
import {
  ActionIcon, Alert, Badge, Button, Card, Collapse, Group, Loader, NumberInput,
  Paper, Progress, Select, SimpleGrid, Stack, Text, Title, Tooltip,
} from '@mantine/core'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { notifications } from '@mantine/notifications'
import { useNavigate, useParams } from 'react-router-dom'
import type { ColumnDef } from '@tanstack/react-table'

import { ApiError, api } from '../api/client'
import type {
  BOMExpanded, BOMLineExpanded, ItemRow, MasterData, PurchaseOrder,
} from '../api/types'
import { useAuth } from '../auth/AuthContext'
import { DataGrid } from '../components/DataGrid'
import { StatusBadge } from '../components/StatusBadge'
import { EM_DASH, fmtDateTime, fmtMoney, fmtPct, fmtQty } from '../format'
import { confirmAction } from '../components/confirm'
import { BomEditorModal } from './BomEditorModal'
import { ItemPicker } from './ItemPicker'

/** Hebrew source tag for a hw_discount_source value — DP-11. */
const HW_DISCOUNT_SOURCE_LABEL: Record<string, string> = {
  derived: 'נגזר',
  override: 'ידני',
  fixed: 'קבוע',
}

export function BomDetailPage() {
  const { id } = useParams()
  const bomId = Number(id)
  const navigate = useNavigate()
  const qc = useQueryClient()
  const { canEdit } = useAuth()
  const [editingHeader, setEditingHeader] = useState(false)
  const [picking, setPicking] = useState(false)

  const expanded = useQuery({
    queryKey: ['bom-expanded', bomId],
    queryFn: () => api.get<BOMExpanded>(`/boms/${bomId}/expanded`),
    enabled: Number.isFinite(bomId),
  })
  const master = useQuery({
    queryKey: ['master-data'],
    queryFn: () => api.get<MasterData>('/master-data'),
  })

  const bom = expanded.data?.bom
  const summary = expanded.data?.summary
  const locked = bom?.status === 'בהזמנה'

  const saveBom = useMutation({
    mutationFn: (next: typeof bom) => api.put(`/boms/${bomId}`, next),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['bom-expanded', bomId] }),
    onError: (err) =>
      notifications.show({
        color: 'red',
        message: err instanceof ApiError ? err.message : 'השמירה נכשלה',
      }),
  })

  const convert = useMutation({
    mutationFn: () => api.post<PurchaseOrder>(`/boms/${bomId}/convert-to-po`),
    onSuccess: (po) => {
      void qc.invalidateQueries()
      notifications.show({ message: `נוצרה הזמנה ${po.po_number}`, color: 'green' })
      navigate(`/orders/${po.id}`)
    },
    onError: (err) =>
      notifications.show({
        color: 'red',
        message: err instanceof ApiError ? err.message : 'ההמרה נכשלה',
      }),
  })

  const reopen = useMutation({
    mutationFn: () => api.post(`/boms/${bomId}/reopen`),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['bom-expanded', bomId] })
      notifications.show({ message: 'ה-BOM נפתח מחדש', color: 'green' })
    },
    onError: (err) =>
      notifications.show({
        color: 'red',
        // `reopen` refuses while a PO still references the BOM. The server's
        // message names the PO, which is the actionable part.
        message: err instanceof ApiError ? err.message : 'הפתיחה מחדש נכשלה',
      }),
  })

  // Keyed by the BOM LINE's own id, not item_id: a termed item legitimately
  // appears on several lines at once (DP-11's whole premise — the operator's
  // hand BOM buys the same FortiGate model at three different terms, three
  // separate lines, same item_id). Keying by item_id here would have edited
  // or removed every one of them at once instead of the one the user touched.
  function setLineQty(lineId: number, qty: number) {
    if (!bom) return
    saveBom.mutate({
      ...bom,
      lines: bom.lines.map((l) => (l.id === lineId ? { ...l, qty_required: qty } : l)),
    })
  }

  function setLineTerm(lineId: number, termMonths: number | null) {
    if (!bom) return
    saveBom.mutate({
      ...bom,
      lines: bom.lines.map((l) => (l.id === lineId ? { ...l, term_months: termMonths } : l)),
    })
  }

  /** Writes BOMLine.hw_discount_override, as a 0.0-1.0 fraction (the input
   * shows/accepts a percent). `null` (the field cleared) returns the line to
   * the engine's derived value. */
  function setLineHwDiscountOverride(lineId: number, fraction: number | null) {
    if (!bom) return
    saveBom.mutate({
      ...bom,
      lines: bom.lines.map((l) => (l.id === lineId ? { ...l, hw_discount_override: fraction } : l)),
    })
  }

  function removeLine(lineId: number) {
    if (!bom) return
    saveBom.mutate({ ...bom, lines: bom.lines.filter((l) => l.id !== lineId) })
  }

  function addItems(items: ItemRow[]) {
    if (!bom) return
    // A termed item is the one case where "already on this BOM" is not a
    // duplicate to refuse — DP-11's own premise is the SAME model bought at
    // several different terms, each its own line. An untermed item keeps the
    // original one-line-per-item behaviour (nothing in this app lets a plain
    // line be usefully split into two, so silently refusing a second pick
    // stays the right default for it).
    const existing = new Set(bom.lines.map((l) => l.item_id))
    const added = items
      .filter((i) => i.id != null && (i.is_termed || !existing.has(i.id)))
      .map((i) => ({
        id: null, bom_id: bomId, item_id: i.id!, qty_required: 1, notes: '',
        term_months: null, hw_discount_override: null,
      }))
    if (added.length) saveBom.mutate({ ...bom, lines: [...bom.lines, ...added] })
    setPicking(false)
  }

  const columns = useMemo<ColumnDef<BOMLineExpanded, any>[]>(
    () => [
      { accessorKey: 'item_name', header: 'פריט', meta: { code: true } },
      { accessorKey: 'sku', header: 'מקט יצרן', meta: { code: true } },
      {
        accessorKey: 'internal_sku',
        header: 'מקט פנימי',
        meta: { code: true },
        cell: (c) => c.getValue<string>() || EM_DASH,
      },
      {
        accessorKey: 'description',
        header: 'תיאור',
        cell: (c) =>
          c.row.original.blocked ? (
            <Text size="sm" c="red">{c.row.original.block_reason}</Text>
          ) : (
            c.getValue<string>() || EM_DASH
          ),
      },
      { accessorKey: 'component_type', header: 'סוג', cell: (c) => c.getValue<string>() || EM_DASH },
      { accessorKey: 'qty_required', header: 'נדרש', meta: { numeric: true }, cell: (c) => fmtQty(c.getValue<number>()) },
      { accessorKey: 'qty_in_stock', header: 'במלאי', meta: { numeric: true }, cell: (c) => fmtQty(c.getValue<number>()) },
      { accessorKey: 'qty_to_purchase', header: 'לרכישה', meta: { numeric: true }, cell: (c) => fmtQty(c.getValue<number>()) },
      {
        accessorKey: 'unit_price_usd',
        header: 'GPL',
        meta: { numeric: true },
        cell: (c) => fmtMoney(c.getValue<number>(), c.row.original.currency),
      },
      {
        accessorKey: 'discount_pct',
        header: 'הנחה%',
        meta: { numeric: true },
        // DP-11: the "depends on the number of years" hardware row — the
        // only row discount_by_term is ever set on in practice — is
        // editable here, with a source tag so the operator can tell a
        // derived value from one they typed over. Every other row (every
        // row of a term-less item, and the licence/support rows of a
        // termed one) stays the plain display it always was.
        cell: (c) => {
          const row = c.row.original
          // Editable only where the engine would honour an override: a
          // hardware row whose discount is derived (or already overridden).
          // A fixed-discount hardware row of a termed item keeps the plain
          // display — the engine ignores hw_discount_override for it, so an
          // input here would accept a value and snap back on the next load.
          const isTermedHardware = row.component_type === 'Hardware'
            && row.terms_available.length > 0
            && row.hw_discount_source !== 'fixed'
          if (!isTermedHardware || !canEdit || locked) {
            return fmtPct(c.getValue<number>() * 100)
          }
          return (
            <Group gap={4} wrap="nowrap" justify="flex-end">
              <NumberInput
                size="xs"
                hideControls
                min={0}
                max={100}
                suffix="%"
                decimalScale={2}
                w={90}
                aria-label="הנחת חומרה"
                value={Number(((row.hw_discount_applied ?? 0) * 100).toFixed(2))}
                onChange={(v) => {
                  // Cleared -> null -> back to the engine's derived value.
                  // Otherwise the typed percent, as a 0.0-1.0 fraction.
                  const fraction = v === '' ? null : (Number(v) || 0) / 100
                  setLineHwDiscountOverride(row.bom_line_id, fraction)
                }}
              />
              <Badge size="xs" variant="light" color={row.hw_discount_source === 'override' ? 'blue' : 'gray'}>
                {HW_DISCOUNT_SOURCE_LABEL[row.hw_discount_source] ?? row.hw_discount_source}
              </Badge>
            </Group>
          )
        },
      },
      {
        id: 'net',
        header: 'מחיר נטו',
        meta: { numeric: true },
        accessorFn: (r) => r.unit_price_usd * (1 - (r.discount_pct || 0)),
        cell: (c) => fmtMoney(c.getValue<number>(), c.row.original.currency),
      },
      {
        id: 'cost',
        header: 'עלות לרכישה',
        meta: { numeric: true },
        accessorFn: (r) => r.qty_to_purchase * r.unit_price_usd * (1 - (r.discount_pct || 0)),
        cell: (c) => fmtMoney(c.getValue<number>(), c.row.original.currency),
      },
      {
        id: 'source',
        header: 'מקור מחיר',
        cell: (c) => {
          const row = c.row.original
          if (row.blocked) {
            return (
              <Tooltip label={row.block_reason}>
                <Badge size="sm" color="red" variant="light">חסום</Badge>
              </Tooltip>
            )
          }
          return row.price_source === 'dealer' ? (
            EM_DASH
          ) : (
            <Tooltip label="אין מחיר דילר לרכיב זה — המחיר לקוח ממחירון הפריט">
              <Badge size="sm" color="yellow" variant="light">⚠ חלופי</Badge>
            </Tooltip>
          )
        },
      },
    ],
    // `bom`: the Hardware-discount cell's NumberInput closes over
    // setLineHwDiscountOverride, which itself closes over `bom` — without
    // this dependency the columns (and the handler they call) would freeze
    // at whatever `bom` looked like on first render.
    [bom, canEdit, locked],
  )

  if (expanded.isLoading) return <Text>טוען…</Text>
  if (!bom || !summary) return <Alert color="red">ה-BOM לא נמצא.</Alert>

  const pct = Math.min(200, Math.round(summary.pct_used))
  const overBudget = summary.over_budget
  const netTotal = summary.total_with_extra_usd
  const orderTotal = summary.order_total_usd
  const stockSaves = orderTotal - netTotal > 0.01

  return (
    <Stack gap="md">
      <Group justify="space-between" align="flex-start">
        <Group gap="sm">
          <Button variant="subtle" size="compact-sm" onClick={() => navigate('/bom')}>
            ← חזרה
          </Button>
          <Title order={2}>{bom.project_name}</Title>
          <StatusBadge value={bom.status} />
        </Group>
        <Group gap="xs">
          {canEdit && !locked && (
            <>
              <Button variant="light" onClick={() => setEditingHeader(true)}>מאפייני BOM</Button>
              <Button variant="light" onClick={() => setPicking(true)}>הוסף פריטים</Button>
            </>
          )}
          <Button variant="default" onClick={() => void api.download(`/boms/${bomId}/export.csv`, `BOM-${bom.project_name}.csv`)}>
            ייצוא CSV
          </Button>
          {/* The portable form: a task document another installation can
              import via "ייבא משימה" on the BOM list. CSV is for people,
              this is for the app. */}
          <Button
            variant="default"
            onClick={() => void api.downloadJson(
              `/boms/${bomId}/task`, `BOM-${bom.project_name}.json`)}
          >
            ייצא משימה (JSON)
          </Button>
          {canEdit && locked && (
            <Button variant="light" onClick={() => reopen.mutate()} loading={reopen.isPending}>
              פתח מחדש
            </Button>
          )}
          {canEdit && !locked && (
            <Tooltip
              label={`לא ניתן להמיר להזמנה — ${summary.blocked_lines} שורות חסומות `
                + '(טרם נבחרה תקופה, או שחסר רכיב מתאים לתקופה שנבחרה)'}
              disabled={summary.blocked_lines === 0}
            >
              {/* A disabled native <button> does not reliably forward hover
                  to Mantine's Tooltip — wrapping it in a plain span is the
                  standard workaround, same reason the span exists here and
                  not on any of this screen's other (never-disabled) buttons. */}
              <span>
                <Button
                  onClick={() => {
                    // Converting commits money and locks the BOM. Consequential
                    // rather than destructive — it is reversible by deleting the
                    // order — so it asks in the app's own voice, not in red.
                    void confirmAction({
                      title: 'המרת BOM להזמנת רכש',
                      message: `להמיר את ה-BOM להזמנת רכש?\n\n`
                        + `סכום ההזמנה: ${fmtMoney(orderTotal)}\n`
                        + `(ההזמנה כוללת את כל הכמויות הנדרשות, ללא ניכוי מלאי)`,
                      confirmLabel: 'המר להזמנה',
                      danger: false,
                    }).then((ok) => ok && convert.mutate())
                  }}
                  loading={convert.isPending}
                  disabled={!bom.lines.length || summary.blocked_lines > 0}
                >
                  המר להזמנה — {fmtMoney(orderTotal)}
                </Button>
              </span>
            </Tooltip>
          )}
        </Group>
      </Group>

      {locked && (
        <Alert color="teal" variant="light">
          ה-BOM הומר להזמנת רכש ואינו ניתן לעריכה. כדי לערוך אותו יש למחוק את
          ההזמנה המקושרת ואז לפתוח אותו מחדש.
        </Alert>
      )}

      {summary.fallback_priced_lines > 0 && (
        <Alert color="yellow" variant="light">
          ל-{summary.fallback_priced_lines} שורות אין מחיר דילר — המחיר נלקח
          ממחירון הפריט ועשוי שלא לשקף את המחיר בפועל.
        </Alert>
      )}

      <SimpleGrid cols={{ base: 1, sm: 2, lg: 4 }}>
        <Card withBorder padding="sm">
          <Text size="xs" c="dimmed">עלות נטו (אחרי ניכוי מלאי)</Text>
          <Text fw={700} size="lg">{fmtMoney(netTotal)}</Text>
          <Text size="xs" c="dimmed">
            ביניים {fmtMoney(summary.subtotal_usd)} · מע"מ {fmtMoney(summary.vat_amount_usd)}
            {summary.shipping_amount_usd > 0 && ` · משלוח ${fmtMoney(summary.shipping_amount_usd)}`}
          </Text>
        </Card>

        <Card withBorder padding="sm">
          <Text size="xs" c="dimmed">סכום ההזמנה (כל הכמויות)</Text>
          <Text fw={700} size="lg">{fmtMoney(orderTotal)}</Text>
          <Text size="xs" c={stockSaves ? 'teal' : 'dimmed'}>
            {stockSaves
              ? `המלאי חוסך ${fmtMoney(orderTotal - netTotal)} מהעלות בפועל`
              : 'אין מלאי לניכוי — זהה לעלות נטו'}
          </Text>
        </Card>

        <Card withBorder padding="sm">
          <Text size="xs" c="dimmed">תקציב</Text>
          <Text fw={700} size="lg">
            {summary.budget_usd > 0 ? fmtMoney(summary.budget_usd) : EM_DASH}
          </Text>
          <Text size="xs" c={overBudget ? 'red' : 'dimmed'}>
            {summary.budget_usd > 0
              ? overBudget
                ? `חריגה של ${fmtMoney(summary.delta_usd)}`
                : `נותרו ${fmtMoney(-summary.delta_usd)}`
              : 'לא הוגדר תקציב'}
          </Text>
        </Card>

        <Card withBorder padding="sm">
          <Text size="xs" c="dimmed">חיסכון מול מחירון</Text>
          <Text fw={700} size="lg">{fmtMoney(summary.savings_usd)}</Text>
          <Text size="xs" c="dimmed">מחירון {fmtMoney(summary.list_price_usd)}</Text>
        </Card>
      </SimpleGrid>

      {summary.budget_usd > 0 && (
        <Paper withBorder p="sm">
          <Group justify="space-between" mb={4}>
            <Text size="sm">ניצול תקציב</Text>
            <Text size="sm" fw={600} c={overBudget ? 'red' : undefined}>{pct}%</Text>
          </Group>
          <Progress value={Math.min(100, pct)} color={overBudget ? 'red' : pct > 85 ? 'yellow' : 'teal'} />
          {/* The bar measures the NET cost, which is the money that will
              actually leave. But converting issues a PO for the FULL quantity,
              so the commitment is the order total — and on a BOM with stock the
              two differ. Reading "42% used, $51,819 left" and then approving an
              order that consumes 62% is exactly the trap the order total exists
              to prevent, so the comparison is spelled out rather than left for
              the reader to do. */}
          {stockSaves && (
            <Group justify="space-between" mt={6}>
              <Text size="xs" c="dimmed">
                ההזמנה עצמה (כל הכמויות) תנצל
              </Text>
              <Text
                size="xs"
                fw={600}
                c={orderTotal > summary.budget_usd ? 'red' : 'dimmed'}
              >
                {Math.round((orderTotal / summary.budget_usd) * 100)}% מהתקציב
                {orderTotal > summary.budget_usd && ' — חריגה'}
              </Text>
            </Group>
          )}
        </Paper>
      )}

      <Paper withBorder p="sm">
        <Group justify="space-between" mb="xs">
          <Title order={5}>שורות ה-BOM</Title>
          <Group gap="xs">
            {/* Quantities autosave. Without a word about it the user has no
                way to tell an edit that stuck from one that did not — on a
                screen where the number is money. */}
            {saveBom.isPending && (
              <Group gap={6}>
                <Loader size="xs" />
                <Text size="xs" c="dimmed">שומר…</Text>
              </Group>
            )}
            {!saveBom.isPending && saveBom.isSuccess && (
              <Text size="xs" c="teal">נשמר ✓</Text>
            )}
            <Text size="sm" c="dimmed">{bom.lines.length} פריטים</Text>
          </Group>
        </Group>
        {bom.lines.length === 0 ? (
          <Text c="dimmed" size="sm">אין פריטים ב-BOM. השתמש ב"הוסף פריטים".</Text>
        ) : (
          <Stack gap={4}>
            {bom.lines.map((line) => {
              // By bom_line_id, NOT item_id: a termed item legitimately sits
              // on several lines at once (DP-11 — the same FortiGate bought
              // at three different terms), each with its own term/blocked
              // state. Matching by item_id would show line #2's status on
              // line #1 whenever they shared an item.
              const first = expanded.data!.lines.find((l) => l.bom_line_id === line.id)
              const termed = (first?.terms_available.length ?? 0) > 0
              return (
                <Stack key={line.id ?? `new-${line.item_id}`} gap={2}>
                  <Group
                    gap="sm" wrap="nowrap"
                    p={first?.blocked ? 6 : undefined}
                    style={first?.blocked
                      ? { backgroundColor: 'var(--mantine-color-red-0)', borderRadius: 4 }
                      : undefined}
                  >
                    <Text style={{ flex: 1 }} size="sm">
                      {first?.item_name ?? `#${line.item_id}`}
                      {first?.description ? ` — ${first.description}` : ''}
                    </Text>
                    {termed && (
                      <Select
                        size="xs"
                        w={130}
                        placeholder="בחר תקופה"
                        aria-label="תקופה"
                        disabled={!canEdit || locked}
                        data={(first?.terms_available ?? []).map((t) => ({
                          value: String(t), label: `${t} חודשים`,
                        }))}
                        value={line.term_months != null ? String(line.term_months) : null}
                        onChange={(v) => setLineTerm(line.id!, v ? Number(v) : null)}
                      />
                    )}
                    <NumberInput
                      size="xs"
                      hideControls
                      w={110}
                      min={0}
                      decimalScale={2}
                      disabled={!canEdit || locked}
                      value={line.qty_required}
                      onChange={(v) => setLineQty(line.id!, Number(v) || 0)}
                    />
                    {canEdit && !locked && (
                      <ActionIcon
                        size={30} color="red" variant="subtle" aria-label="הסר שורה"
                        loading={saveBom.isPending &&
                                 saveBom.variables?.lines?.length ===
                                   bom.lines.length - 1}
                        onClick={() => removeLine(line.id!)}
                      >
                        ✕
                      </ActionIcon>
                    )}
                  </Group>
                  {first?.blocked && (
                    <Text size="xs" c="red" pr={6}>{first.block_reason}</Text>
                  )}
                </Stack>
              )
            })}
          </Stack>
        )}
      </Paper>

      {summary.blocked_lines > 0 && (
        <Alert color="red" variant="light">
          {summary.blocked_lines} שורות חסומות — בחר תקופה
        </Alert>
      )}

      <Paper withBorder>
        <Group justify="space-between" p="sm" pb={0}>
          <Title order={5}>פירוט רכיבים לרכישה</Title>
        </Group>
        <DataGrid
          data={expanded.data!.lines}
          columns={columns}
          empty="אין רכיבים להצגה"
          maxHeight="45vh"
          // 13 columns of Hebrew headers and money, one of them now an
          // inline NumberInput + badge (the hardware-discount cell). Below
          // this they compress until a description wraps one word per line.
          minWidth={1650}
        />
      </Paper>

      <StatusHistory bomId={bomId} />

      <BomEditorModal
        opened={editingHeader}
        bom={bom}
        master={master.data}
        onClose={() => setEditingHeader(false)}
      />
      <ItemPicker
        opened={picking}
        onClose={() => setPicking(false)}
        onPick={addItems}
        // Hide an already-added item from the list UNLESS it is termed — a
        // termed item stays pickable so the operator can add it again at a
        // different term (see addItems's own comment).
        excludeIds={bom.lines
          .filter((l) => {
            const row = expanded.data!.lines.find((x) => x.item_id === l.item_id)
            return !(row?.terms_available.length ?? 0)
          })
          .map((l) => l.item_id)}
      />
    </Stack>
  )
}

/**
 * When this BOM changed status, and why.
 *
 * A converted BOM is locked, and "פתח מחדש" is refused while a PO exists — so
 * the two questions someone actually has in front of a locked BOM are when it
 * was converted and by which step. `bom_status_log` has held that all along
 * and nothing displayed it.
 *
 * Collapsed by default: it is context for a question, not part of reading the
 * BOM, and it costs a request nobody asked for if left open.
 */
function StatusHistory({ bomId }: { bomId: number }) {
  const [open, setOpen] = useState(false)
  const log = useQuery({
    queryKey: ['bom-status-log', bomId],
    queryFn: () =>
      api.get<{ old_status: string; new_status: string; changed_at: string; notes: string }[]>(
        `/boms/${bomId}/status-log`),
    enabled: open,
  })

  return (
    <Paper withBorder p="sm">
      <Group justify="space-between">
        <Title order={5}>היסטוריית סטטוס</Title>
        {/* compact-sm, not compact-xs: the xs compact size measures 37x22 —
            under the 24px target (uxaudit.mjs's new /bom/:id probe caught
            it, at every width, since it is not a responsive squeeze). Same
            size already used for this page's own back button below. */}
        <Button size="compact-sm" variant="subtle" onClick={() => setOpen((o) => !o)}>
          {open ? 'הסתר' : 'הצג'}
        </Button>
      </Group>
      <Collapse expanded={open}>
        <Stack gap={4} mt="xs">
          {log.isLoading && <Text size="sm" c="dimmed">טוען…</Text>}
          {log.data?.length === 0 && (
            <Text size="sm" c="dimmed">אין שינויי סטטוס רשומים.</Text>
          )}
          {log.data?.map((row, i) => (
            <Group key={i} gap="sm" wrap="nowrap">
              <Text size="xs" c="dimmed" style={{ whiteSpace: 'nowrap' }}>
                {fmtDateTime(row.changed_at)}
              </Text>
              <Text size="sm">
                {row.old_status || EM_DASH} ← {row.new_status}
              </Text>
              {row.notes && <Text size="xs" c="dimmed">{row.notes}</Text>}
            </Group>
          ))}
        </Stack>
      </Collapse>
    </Paper>
  )
}
